import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import SocketContext from 'utils/hooks/useSocket';
import { useWalletData } from 'context/WalletDataContext';
import { useTradeUpdate } from 'context/TradeUpdateContext';
import { makeMargin, makeTrade } from 'api/socketService';
import { formatCurrency } from 'utils';
import { CASH_TICKER, baseOf, cashBalance, quoteOf } from 'utils/cash';
import SetTPSL from 'components/ui/SetTPSL';

import {
  Trade,
  SideTabs,
  SideTab,
  ModeTabs,
  ModeTab,
  LevRow,
  LevChip,
  Field,
  Pcts,
  PctBtn,
  Meta,
  CTA,
  TpslButton,
  TpslOverlay,
  TpslModal,
  TpslClose,
  ErrorBanner,
} from './styled';

type OrderType = 'margin' | 'spot';
type Side = 'long' | 'short';
type Mode = 'Market' | 'Limit' | 'TP/SL';

interface OrderPanelProps {
  tournamentDetails: any;
  type: OrderType;
}

const TRADE_DEBOUNCE_MS = 2000;

const OrderPanel = ({ tournamentDetails, type }: OrderPanelProps) => {
  const isMargin = type === 'margin';

  const [side, setSide] = useState<Side>('long');
  const [mode, setMode] = useState<Mode>('Market');
  const [leverage, setLeverage] = useState<number>(1);
  const [usdtAmount, setUsdtAmount] = useState(0);
  const [usdtInputValue, setUsdtInputValue] = useState('');
  const [coinInputValue, setCoinInputValue] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [tpsl, setTpsl] = useState({ tp: '', sl: '' });
  const [tpslOpen, setTpslOpen] = useState(false);
  const [inputFocused, setInputFocused] = useState<string | null>(null);
  const [isTrading, setIsTrading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const lastClickTimestamp = useRef(0);

  const { selectedCoin, selectedCoinValue } = useContext(SocketContext) as any;
  const { walletData, update } = useWalletData();
  const { triggerTradeUpdate } = useTradeUpdate();

  // TEAM_003: Support both legacy and flattened tournament shapes.
  const rawData: any = tournamentDetails?.data;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
  const leverageOptions: number[] = useMemo(() => {
    const fromAttrs = attributes?.leverage;
    if (Array.isArray(fromAttrs) && fromAttrs.length > 0) {
      return fromAttrs.map((l: any) => Number(String(l).replace('x', ''))).filter((n: number) => !Number.isNaN(n));
    }
    return [1, 2, 5, 10, 25, 50];
  }, [attributes?.leverage]);

  const availableCash = cashBalance(walletData);

  const shortNameSelectedCoin = baseOf(selectedCoin?.name) ?? '';

  // Spot Sell needs the base-coin balance, not cash, because the user is
  // disposing of what they're holding. Margin always settles in cash so the
  // collateral readout stays cash regardless of side.
  const baseWallet = Array.isArray(walletData) && shortNameSelectedCoin
    ? walletData.find((w: any) => w?.ticker?.short_name === shortNameSelectedCoin)
    : undefined;
  const availableBaseCoin = baseWallet ? Number(baseWallet.amount) : 0;
  const isSpotSell = !isMargin && side === 'short';
  const rawPrice = (selectedCoinValue as any)?.last;
  const marketPrice = typeof rawPrice === 'number' && Number.isFinite(rawPrice) && rawPrice > 0 ? rawPrice : 0;

  // Spendable cash-equivalent for the current side:
  // - Buy / Margin: the cash wallet (you spend cash directly).
  // - Spot Sell: the base coin you hold, valued at the live market price.
  //   (Use marketPrice rather than previewEntryPrice — the % / MAX buttons
  //   reflect what the user *holds* now, not the conditional price the
  //   eventual limit order might fill at.)
  const maxUsdtValue = isSpotSell && marketPrice > 0
    ? availableBaseCoin * marketPrice
    : availableCash;
  let precision = selectedCoin?.base?.data?.attributes?.precision ?? selectedCoin?.precision ?? 4;
  if (precision > 8) precision = 8;

  // TEAM_006: For long LIMIT, marketable (limit >= market) fills immediately at market.
  const parsedLimitPrice = Number(limitPrice);
  const hasValidLimitPrice = mode === 'Limit' && Number.isFinite(parsedLimitPrice) && parsedLimitPrice > 0;
  const previewEntryPrice = hasValidLimitPrice
    ? marketPrice > 0
      ? side === 'long'
        ? parsedLimitPrice >= marketPrice
          ? marketPrice
          : parsedLimitPrice
        : parsedLimitPrice <= marketPrice
        ? marketPrice
        : parsedLimitPrice
      : parsedLimitPrice
    : marketPrice;

  // Approximate liquidation price for margin preview. Backend uses its own
  // formula; this is just an indicator while composing the order.
  const liqPrice = useMemo(() => {
    if (!isMargin || !previewEntryPrice || leverage <= 0) return null;
    const buffer = 0.95 / leverage;
    return side === 'long'
      ? previewEntryPrice * (1 - buffer)
      : previewEntryPrice * (1 + buffer);
  }, [isMargin, previewEntryPrice, leverage, side]);

  // Sync USDT/coin amounts with rangeval & limit price.
  useEffect(() => {
    if (inputFocused) return;
    const safeUsdtAmount = Number.isFinite(usdtAmount) ? usdtAmount : 0;
    const newCoin = previewEntryPrice > 0 ? safeUsdtAmount / previewEntryPrice : 0;
    const safeCoin = Number.isFinite(newCoin) ? newCoin : 0;
    setCoinInputValue(safeCoin.toFixed(precision));
    setUsdtInputValue(safeUsdtAmount.toFixed(2));
  }, [usdtAmount, previewEntryPrice, inputFocused, precision]);

  // Mobile-only: signal Limit-active via body class so the chart calc shrinks.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cls = 'mobile-limit-active';
    const mq = window.matchMedia('(max-width: 720px)');
    const apply = () => {
      const shouldBe = mq.matches && isMargin && mode === 'Limit';
      if (shouldBe) document.body.classList.add(cls);
      else document.body.classList.remove(cls);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.classList.remove(cls);
    };
  }, [isMargin, mode]);

  useEffect(() => {
    if (errorMsg) setErrorMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side, leverage, usdtAmount, tpsl.tp, tpsl.sl, limitPrice]);

  const handleUsdtChange = (raw: string) => {
    setUsdtInputValue(raw);
    const numeric = raw === '' ? 0 : Number(raw);
    const safe = Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
    const capped = safe > maxUsdtValue ? maxUsdtValue : safe;
    setUsdtAmount(capped);
    if (capped !== safe) setUsdtInputValue(capped.toFixed(2));
  };

  const handleCoinChange = (raw: string) => {
    setCoinInputValue(raw);
    const numeric = raw === '' ? 0 : Number(raw);
    const safeNumeric = Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
    // On spot Sell clamp directly against base-coin holdings — converting
    // through usdtAmount loses precision and would silently allow oversell
    // when the market price ticks between read and clamp.
    const coinCap = isSpotSell ? availableBaseCoin : Number.POSITIVE_INFINITY;
    const cappedCoinInput = safeNumeric > coinCap ? coinCap : safeNumeric;
    let next = previewEntryPrice > 0 ? cappedCoinInput * previewEntryPrice : 0;
    if (next > maxUsdtValue) {
      next = maxUsdtValue;
      const recomputedCoin = previewEntryPrice > 0 ? next / previewEntryPrice : 0;
      setCoinInputValue(Number.isFinite(recomputedCoin) ? recomputedCoin.toFixed(precision) : '0');
    } else if (cappedCoinInput !== safeNumeric) {
      setCoinInputValue(cappedCoinInput.toFixed(precision));
    }
    setUsdtAmount(Number.isFinite(next) ? next : 0);
  };

  const handlePctClick = (pct: number) => {
    const next = (pct / 100) * maxUsdtValue;
    setUsdtAmount(Number.isFinite(next) ? next : 0);
  };

  const handleMaxClick = () => {
    setUsdtAmount(maxUsdtValue);
  };

  const resetForm = () => {
    setUsdtAmount(0);
    setUsdtInputValue('');
    setCoinInputValue('');
    setLimitPrice('');
    setLeverage(1);
    setTpsl({ tp: '', sl: '' });
  };

  const handleSubmit = () => {
    if (isTrading) return;
    const now = Date.now();
    if (now - lastClickTimestamp.current < TRADE_DEBOUNCE_MS) return;
    lastClickTimestamp.current = now;
    setIsTrading(true);

    const onSuccess = () => {
      update();
      triggerTradeUpdate();
      resetForm();
      setIsTrading(false);
    };

    const onError = (err: string) => {
      // eslint-disable-next-line no-console
      console.error(err);
      setErrorMsg(err);
      update();
      triggerTradeUpdate();
      setIsTrading(false);
    };

    if (isMargin) {
      const tp = tpsl.tp ? Number(tpsl.tp) : undefined;
      const sl = tpsl.sl ? Number(tpsl.sl) : undefined;
      const market = (selectedCoinValue as any)?.last;

      if (typeof market === 'number' && market > 0) {
        if (side === 'long') {
          if (tp !== undefined && tp <= market) {
            onError(`Take Profit (${tp}) must be greater than market price (${market.toFixed(2)}) for Long`);
            return;
          }
          if (sl !== undefined && sl >= market) {
            onError(`Stop Loss (${sl}) must be less than market price (${market.toFixed(2)}) for Long`);
            return;
          }
        } else {
          if (tp !== undefined && tp >= market) {
            onError(`Take Profit (${tp}) must be less than market price (${market.toFixed(2)}) for Short`);
            return;
          }
          if (sl !== undefined && sl <= market) {
            onError(`Stop Loss (${sl}) must be greater than market price (${market.toFixed(2)}) for Short`);
            return;
          }
        }
      }

      const payload = {
        tradingPair: selectedCoin?.name,
        transactionType: side,
        leverage,
        usedFunds: Number(usdtAmount.toFixed(2)),
        token: uuidv4(),
        ...(mode === 'Limit' && hasValidLimitPrice ? { limit: parsedLimitPrice } : {}),
        ...(tp !== undefined ? { takeProfit: tp } : {}),
        ...(sl !== undefined ? { stopLoss: sl } : {}),
      };
      makeMargin(payload, (response: any) => {
        if (response?.error) {
          onError(response.error);
        } else {
          onSuccess();
        }
      });
    } else {
      const payload = {
        tournament: rawData?.id,
        tradingPair: selectedCoin?.name,
        ticker: quoteOf(selectedCoin?.name),
        type: side === 'long' ? 'buy' : 'sell',
        value: Number(usdtAmount.toFixed(2)),
        token: uuidv4(),
      };
      makeTrade(payload, (response: any) => {
        if (response?.error) {
          onError(response.error);
        } else {
          onSuccess();
        }
      });
    }
  };

  const ctaDisabled = isTrading || maxUsdtValue <= 0 || usdtAmount <= 0;
  const ctaLabel = isTrading
    ? 'Loading…'
    : isMargin
    ? side === 'long'
      ? `Open Long · ${shortNameSelectedCoin || 'BTC'}`
      : `Open Short · ${shortNameSelectedCoin || 'BTC'}`
    : side === 'long'
    ? `Buy ${shortNameSelectedCoin || 'BTC'}`
    : `Sell ${shortNameSelectedCoin || 'BTC'}`;

  return (
    <Trade>
      <SideTabs>
        <SideTab
          type="button"
          className={`long ${side === 'long' ? 'on' : ''}`}
          onClick={() => setSide('long')}
        >
          {isMargin ? 'Open Long' : 'Buy'}
        </SideTab>
        <SideTab
          type="button"
          className={`short ${side === 'short' ? 'on' : ''}`}
          onClick={() => setSide('short')}
        >
          {isMargin ? 'Open Short' : 'Sell'}
        </SideTab>
      </SideTabs>

      {isMargin && (
        <ModeTabs>
          {(['Market', 'Limit', 'TP/SL'] as Mode[]).map((m) => (
            <ModeTab
              key={m}
              type="button"
              className={mode === m ? 'on' : ''}
              onClick={() => setMode(m)}
            >
              {m}
            </ModeTab>
          ))}
        </ModeTabs>
      )}

      {isMargin && (
        <LevRow>
          {leverageOptions.map((l) => (
            <LevChip
              key={l}
              type="button"
              $shortSide={side === 'short'}
              className={leverage === l ? 'on' : ''}
              onClick={() => setLeverage(l)}
            >
              {l}x
            </LevChip>
          ))}
        </LevRow>
      )}

      {isMargin && mode === 'Limit' && (
        <Field>
          <span className="l">Limit price</span>
          <input
            className="v"
            type="number"
            inputMode="decimal"
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value)}
          />
          <span className="u">{CASH_TICKER}</span>
        </Field>
      )}

      <Field>
        <span className="l">Amount</span>
        <input
          className="v"
          type="number"
          inputMode="decimal"
          value={usdtInputValue}
          onFocus={() => {
            setInputFocused('USDT');
            setUsdtInputValue('');
          }}
          onBlur={() => {
            setInputFocused(null);
            if (usdtInputValue === '') setUsdtAmount(0);
          }}
          onChange={(e) => handleUsdtChange(e.target.value)}
        />
        <span className="u">{CASH_TICKER}</span>
      </Field>

      <Field>
        <span className="l">Size</span>
        <input
          className="v"
          type="number"
          inputMode="decimal"
          value={coinInputValue}
          onFocus={() => {
            setInputFocused(shortNameSelectedCoin);
            setCoinInputValue('');
          }}
          onBlur={() => {
            setInputFocused(null);
            if (coinInputValue === '') setUsdtAmount(0);
          }}
          onChange={(e) => handleCoinChange(e.target.value)}
        />
        <span className="u">{shortNameSelectedCoin || 'BTC'}</span>
      </Field>

      {isMargin && mode === 'TP/SL' && (
        <TpslButton type="button" onClick={() => setTpslOpen(true)}>
          <span className="l">Take profit / Stop loss</span>
          <span className="v">
            {tpsl.tp || '—'} / {tpsl.sl || '—'}
            <span className="chev"> ›</span>
          </span>
        </TpslButton>
      )}

      <Pcts>
        {[25, 50, 75].map((p) => (
          <PctBtn key={p} type="button" onClick={() => handlePctClick(p)}>
            {p}%
          </PctBtn>
        ))}
        <PctBtn type="button" onClick={handleMaxClick}>
          MAX
        </PctBtn>
      </Pcts>

      <Meta>
        <span>Available</span>
        <b>
          {isSpotSell
            ? `${availableBaseCoin.toFixed(precision)} ${shortNameSelectedCoin}`
            : `${formatCurrency(availableCash)} ${CASH_TICKER}`}
        </b>
      </Meta>

      {isMargin && liqPrice !== null && Number.isFinite(liqPrice) && marketPrice > 0 && (
        <Meta>
          <span>Liq. price</span>
          <b className={side === 'long' ? 'tb-neg' : 'tb-pos'}>
            {formatCurrency(liqPrice)}
          </b>
        </Meta>
      )}

      {errorMsg && <ErrorBanner role="alert">{errorMsg}</ErrorBanner>}

      <CTA
        type="button"
        $side={side}
        disabled={ctaDisabled}
        onClick={handleSubmit}
      >
        {ctaLabel}
      </CTA>

      {tpslOpen && (
        <TpslOverlay onClick={() => setTpslOpen(false)}>
          <TpslModal onClick={(e) => e.stopPropagation()}>
            <TpslClose type="button" onClick={() => setTpslOpen(false)} />
            <SetTPSL
              takeProfit={tpsl.tp}
              setTakeProfit={(v: string) => setTpsl((s) => ({ ...s, tp: v }))}
              stopLoss={tpsl.sl}
              setStopLoss={(v: string) => setTpsl((s) => ({ ...s, sl: v }))}
              onAccept={() => setTpslOpen(false)}
              onCancel={() => setTpslOpen(false)}
              tradeId={null}
            />
          </TpslModal>
        </TpslOverlay>
      )}
    </Trade>
  );
};

export default OrderPanel;
